import { useState, useMemo } from 'react'

import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { logger } from 'src/lib/logger'

import DomainAvatar from '../DomainAvatar/DomainAvatar'

export const ALL_WORKSPACES_WITH_DOMAINS = gql`
  query AllWorkspacesWithDomains {
    allWorkspacesWithDomains {
      id
      name
      domains {
        domain
      }
    }
  }
`

interface WorkspaceWithDomains {
  id: string
  name: string
  domains: Array<{ domain: string }>
}

interface WorkspaceFinderProps {
  currentWorkspaceId?: string
  onWorkspaceSelect?: (workspace: WorkspaceWithDomains) => void
}

const optionContainerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 0.5,
}

const workspaceNameSx = {
  fontWeight: 600,
  fontSize: '0.875rem',
}

const renderInputSx = {
  '& .MuiAutocomplete-input': {
    fontSize: '0.875rem',
  },
}

const WorkspaceFinder = ({
  currentWorkspaceId,
  onWorkspaceSelect,
}: WorkspaceFinderProps) => {
  const [value, setValue] = useState<WorkspaceWithDomains | null>(null)

  const { data, loading, error } = useQuery(ALL_WORKSPACES_WITH_DOMAINS, {
    onCompleted: (data) => {
      logger.dev('WorkspaceFinder: Query completed', {
        workspacesCount: data?.allWorkspacesWithDomains?.length || 0,
      })
    },
    onError: (error) => {
      logger.error('WorkspaceFinder: Query error', {
        error: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      })
    },
  })

  const workspaces: WorkspaceWithDomains[] = useMemo(() => {
    const result = data?.allWorkspacesWithDomains || []
    logger.dev('WorkspaceFinder: Workspaces updated', {
      count: result.length,
      currentWorkspaceId,
    })
    return result
  }, [data, currentWorkspaceId])

  const currentWorkspace = useMemo(() => {
    return workspaces.find((ws) => ws.id === currentWorkspaceId) || null
  }, [workspaces, currentWorkspaceId])

  const handleWorkspaceChange = (
    _event: React.SyntheticEvent,
    newValue: WorkspaceWithDomains | null
  ) => {
    if (newValue) {
      setValue(newValue)
      if (onWorkspaceSelect) {
        onWorkspaceSelect(newValue)
      } else {
        // Default navigation behavior
        navigate(routes.adminWorkspace({ workspaceId: newValue.id }))
      }
    }
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography
          color="error"
          variant="body2"
        >
          Error loading workspaces
        </Typography>
      </Box>
    )
  }

  return (
    <Autocomplete
      options={workspaces}
      value={value || currentWorkspace}
      onChange={handleWorkspaceChange}
      loading={loading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      sx={{ width: '100%', minWidth: 200 }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search workspaces..."
          size="small"
          sx={renderInputSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
        >
          <Box sx={optionContainerSx}>
            {option.domains?.length > 0 && option.domains[0]?.domain ? (
              <DomainAvatar
                domain={option.domains[0].domain}
                size={24}
                borderRadius={100}
              />
            ) : (
              <Box sx={{ width: 24, height: 24 }} />
            )}
            <Typography sx={workspaceNameSx}>{option.name}</Typography>
          </Box>
        </Box>
      )}
    />
  )
}

export default WorkspaceFinder
